import { useState } from 'react';
import { Badge } from '@/components/shadcn/badge';
import { Button } from '@/components/shadcn/button';
import Icon from '@/components/icon';
import { sourceMeta } from '@/components/report/source-meta';
import { useAuth } from '@/context/auth-context';
import { useFirebase } from '@/context/firebase-context';
import type { DrugSearch } from '@/types/report';
import { v4 as uuidv4 } from 'uuid';

const STAGE_LABELS: Record<string, string> = {
  resolve: 'Rozpoznawanie leku',
  sources: 'Przeszukiwanie źródeł',
  synthesis: 'Porządkowanie informacji',
  finalize: 'Kończenie raportu',
  peer_wait: 'Czekamy, aż zakończy się inne wyszukiwanie tego leku',
  timeout: 'Przekroczony limit czasu',
  server: 'Błąd serwera',
};

/** Hide raw exception strings; keep short product / classification reasons. */
function isDoctorSafeReason(reason: string | undefined): boolean {
  if (!reason) return false;
  const trimmed = reason.trim();
  if (trimmed.length > 160) return false;
  if (/Error|Exception|Traceback|at 0x|File "|line \d+/i.test(trimmed)) {
    return false;
  }
  // English exception-ish fragments from the backend
  if (/^[A-Z][a-zA-Z]+Error\b/.test(trimmed)) return false;
  if (/\bboom\b|\bfailed\b|\braise\b/i.test(trimmed) && !/[ąćęłńóśźż]/i.test(trimmed)) {
    return false;
  }
  return true;
}

function stageLabel(stage: string | undefined): string | null {
  if (!stage) return null;
  return STAGE_LABELS[stage] ?? stage;
}

export function DrugSearchError({
  results,
  searchId,
  query,
  onRetry,
  retrying = false,
}: {
  results?: DrugSearch['results'];
  searchId?: string;
  query?: string;
  onRetry?: () => void;
  retrying?: boolean;
}) {
  const sourceAvailability = results?.source_availability;
  const stage = stageLabel(results?.stage);
  const showReason = isDoctorSafeReason(results?.reason);
  const { user } = useAuth();
  const { addDocument } = useFirebase();
  const [reportState, setReportState] = useState<'idle' | 'sending' | 'sent' | 'error'>(
    'idle',
  );

  const reportProblem = async () => {
    if (!user || reportState === 'sending' || reportState === 'sent') return;
    setReportState('sending');
    try {
      const id = uuidv4();
      const payload: Record<string, unknown> = {
        id,
        scope: 'search',
        sentiment: 'negative',
        category: 'other',
        drug_id: results?.drug_id ?? null,
        report_version: results?.report_version ?? null,
        query: query ?? null,
        search_id: searchId ?? null,
        trace_id: results?.trace_id ?? null,
        error_kind: results?.reason ?? results?.error ?? 'failed',
        user_id: user.uid,
        status: 'new',
        created_at: new Date().toISOString(),
        text: 'Nieudane wyszukiwanie',
      };
      await addDocument('feedback', payload, id);
      setReportState('sent');
    } catch (error) {
      console.error('Failed to submit failure feedback:', error);
      setReportState('error');
    }
  };

  return (
    <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-5 shadow-sm sm:p-7">
      <div className="mb-2 flex items-center gap-2.5">
        <Icon
          name="circle-alert"
          className="h-[18px] w-[18px] flex-shrink-0 text-destructive"
        />
        <h3 className="m-0 border-0 font-serif text-xl font-semibold tracking-tight text-destructive">
          {results?.error || 'Nie udało się przygotować raportu.'}
        </h3>
      </div>

      {showReason && (
        <p className="text-sm leading-6 text-muted-foreground">{results?.reason}</p>
      )}

      {stage && (
        <p className="mt-2 text-sm text-muted-foreground">
          Etap: {stage}
        </p>
      )}

      {sourceAvailability && Object.keys(sourceAvailability).length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {Object.entries(sourceAvailability).map(([source, available]) => (
            <Badge
              key={source}
              variant="outline"
              className={`text-xs font-medium ${
                available
                  ? 'border-success/30 bg-success/10 text-success'
                  : 'border-border bg-muted text-muted-foreground'
              }`}
            >
              {sourceMeta(source).label}: {available ? 'dostępne' : 'brak'}
            </Badge>
          ))}
        </div>
      )}

      {results?.trace_id && (
        <p className="mt-3 font-mono text-xs text-muted-foreground">
          ID zgłoszenia: {results.trace_id.slice(0, 12)}
        </p>
      )}

      <div className="mt-5 flex flex-wrap items-center gap-3">
        {onRetry && (
          <Button
            type="button"
            onClick={onRetry}
            disabled={retrying}
            className="h-10 font-medium"
          >
            {retrying ? 'Ponawiamy…' : 'Spróbuj ponownie'}
          </Button>
        )}
        <Button
          type="button"
          variant="outline"
          onClick={reportProblem}
          disabled={reportState === 'sending' || reportState === 'sent'}
          className="h-10"
        >
          {reportState === 'sent'
            ? 'Zgłoszono'
            : reportState === 'sending'
              ? 'Wysyłanie…'
              : 'Zgłoś problem'}
        </Button>
      </div>

      {reportState === 'error' && (
        <p className="mt-2 text-sm text-destructive" role="alert">
          Nie udało się wysłać zgłoszenia. Spróbuj ponownie za chwilę.
        </p>
      )}
      {reportState === 'sent' && (
        <p className="mt-2 text-sm text-brand" role="status">
          Dziękujemy, zgłoszenie do nas dotarło.
        </p>
      )}
    </div>
  );
}
